import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";

import { faPaperPlane } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useAskStream } from "../hooks/useAskStream";

import { isUploadInFlight } from "../utils/is-upload-in-flight";

import { useAskPerformYardStore } from "../AskPerformYardStore";

// Maximum character limit for user input
const MAX_INPUT_LENGTH = 4000;

export const ChatInput: React.FC = () => {
	const inputValue = useAskPerformYardStore((state) => state.inputValue);
	const setInputValue = useAskPerformYardStore((state) => state.setInputValue);
	const status = useAskPerformYardStore((state) => state.status);
	const messages = useAskPerformYardStore((state) => state.messages);
	const scrollToBottom = useAskPerformYardStore((state) => state.scrollToBottom);
	const hasNoDocuments = useAskPerformYardStore((state) => state.hasNoDocuments);
	const askHrEnabled = useAskPerformYardStore((state) => state.cachedAskHrEnabled);
	const goalCreationEnabled = useAskPerformYardStore((state) => state.cachedGoalCreationEnabled);
	const formCreationEnabled = useAskPerformYardStore((state) => state.cachedFormCreationEnabled);
	const goalCoachingEnabled = useAskPerformYardStore((state) => state.cachedGoalCoachingEnabled);
	const formBuildingCoachEnabled = useAskPerformYardStore((state) => state.cachedFormBuildingCoachEnabled);
	const isOnGoalCreationPage = useAskPerformYardStore((state) => state.isOnGoalCreationPage);
	const isOnFormBuilderPage = useAskPerformYardStore((state) => state.isOnFormBuilderPage);
	const { sendQuery, isStreaming } = useAskStream();
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const wasStreamingRef = useRef(false);

	// Only disable for no-documents when Ask HR is on and no other capability can compensate
	const isBlockedByNoDocuments =
		askHrEnabled &&
		hasNoDocuments &&
		!goalCreationEnabled &&
		!formCreationEnabled &&
		!(isOnGoalCreationPage && goalCoachingEnabled) &&
		!(isOnFormBuilderPage && formBuildingCoachEnabled);
	const isUploading = isUploadInFlight(messages);
	const isDisabled = isStreaming || status === "error" || isBlockedByNoDocuments || isUploading;
	const canSend = inputValue.trim().length > 0 && !isDisabled;

	const handleSubmit = useCallback(
		(e?: React.FormEvent) => {
			e?.preventDefault();

			if (!canSend) return;

			const query = inputValue.trim();

			// Get conversation history for context (excluding greeting and current input)
			const conversationHistory = messages
				.filter((m) => m.id !== "greeting")
				.map((m) => ({
					role: m.role as "user" | "assistant",
					content: m.content,
				}));

			sendQuery({
				query,
				messages: conversationHistory,
			});

			// Scroll to bottom when sending a message
			scrollToBottom?.();
		},
		[canSend, inputValue, messages, sendQuery, scrollToBottom]
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			// Submit on Enter (without Shift)
			if (e.key === "Enter" && !e.shiftKey) {
				e.preventDefault();
				handleSubmit();
			}
		},
		[handleSubmit]
	);

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			setInputValue(e.target.value);
		},
		[setInputValue]
	);

	// Auto-resize textarea when content changes
	useLayoutEffect(() => {
		const textarea = textareaRef.current;
		if (textarea) {
			textarea.style.height = "auto";
			textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
		}
	}, [inputValue]);

	// Autofocus textarea when panel opens
	useEffect(() => {
		textareaRef.current?.focus();
	}, []);

	// Refocus textarea when streaming completes
	useEffect(() => {
		if (wasStreamingRef.current && !isStreaming) {
			textareaRef.current?.focus();
		}
		wasStreamingRef.current = isStreaming;
	}, [isStreaming]);

	return (
		<div className="flex flex-col gap-2 px-4 pt-3 pb-4">
			<form onSubmit={handleSubmit} className="box-border flex w-full items-end gap-2">
				<div className="box-border flex flex-1 flex-col items-start gap-2">
					<textarea
						ref={textareaRef}
						value={inputValue}
						onChange={handleChange}
						onKeyDown={handleKeyDown}
						placeholder="Ask PerformYard"
						disabled={isDisabled}
						className="box-border h-[36px] max-h-[120px] min-h-[36px] w-full resize-none overflow-hidden rounded border border-neutral-300 bg-white px-3 py-2 text-sm placeholder:text-neutral-400 hover:overflow-y-auto focus:overflow-y-auto focus:border-transparent focus:ring-2 focus:ring-primary-5 focus:outline-hidden disabled:cursor-not-allowed disabled:bg-neutral-50 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-track]:bg-transparent"
						rows={1}
						maxLength={MAX_INPUT_LENGTH}
						aria-label="Ask a question"
					/>
				</div>
				<button
					type="submit"
					disabled={!canSend}
					aria-label="Send message"
					className={`flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded border-none bg-primary-5 transition-all ${
						canSend
							? "cursor-pointer hover:bg-primary-7 active:bg-primary-8"
							: "cursor-not-allowed bg-neutral-300"
					}`}
				>
					<FontAwesomeIcon icon={faPaperPlane} className="text-base text-white" />
				</button>
			</form>

			<p className="w-full text-center text-xs text-neutral-400">
				AI may make mistakes. Please verify important information.
			</p>
		</div>
	);
};

export default ChatInput;
